import { InspectionEvent } from 'xstate';
import { useRouter } from 'next/router';
import { useState, useCallback, useRef } from 'react';
import {
  getRouteFromStateValue,
  getRouteStateMeta,
  handleRouteServiceDebugLogging,
  getQueryParamsFromParsedUrlQuery,
} from '../utils';
import { RouteMachineServiceSnapshots } from '../RouteMachineService';
import { usePrefetch } from './usePrefetch';
import { useTrackPageView } from './useTrackPageView';

// Intermediate routing states that should not route
export const transitionalStates: string[] = ['initializing'];

interface UseHandleOnTransition {
  isTransitioning: boolean;
  handleStateRouteTransition: (inspectionEvent: InspectionEvent) => void;
}

/**
 * Handle route transitions based on the current machine state
 * @returns UseHandleOnTransition
 */
export const useHandleStateRouteTransition = (): UseHandleOnTransition => {
  const { push, query } = useRouter();
  const prefetch = usePrefetch();
  const trackPageView = useTrackPageView();
  const [isTransitioning, setIsTransitioning] = useState<boolean>(true);
  const previousStateValue = useRef<string | null>(null);

  const handleStateRouteTransition = useCallback(
    async (inspectionEvent: InspectionEvent) => {
      if (inspectionEvent.type !== '@xstate.microstep') {
        return;
      }
      const state = inspectionEvent.snapshot as RouteMachineServiceSnapshots;

      // Prevent self transitions
      if (state.value === previousStateValue.current) {
        return;
      }

      setIsTransitioning(true);

      let stateRoute = getRouteFromStateValue(state.value, query);
      stateRoute = getQueryParamsFromParsedUrlQuery(stateRoute, query);

      // Transitional routes are not routeable
      if (!transitionalStates.includes(stateRoute)) {
        await push(stateRoute);
        previousStateValue.current = state.value;

        const meta = getRouteStateMeta('router', state);

        trackPageView(meta.analyticsPageName);
        prefetch(meta.prefetchRoutes);
      }

      handleRouteServiceDebugLogging(state.value, inspectionEvent.event);

      setIsTransitioning(false);
    },
    []
  );

  return { isTransitioning, handleStateRouteTransition };
};
